// utils/securityConfig.js
class SecurityConfig {
  constructor() {
    this.environment = process.env.REACT_APP_ENVIRONMENT || 'development';
    this.enableCSP = process.env.REACT_APP_ENABLE_CSP === 'true';
    this.enableHSTS = process.env.REACT_APP_ENABLE_HSTS === 'true';
  }

  // Configurar Content Security Policy
  configureCSP() {
    if (!this.enableCSP) return;

    const cspPolicy = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' https://cdn.jsdelivr.net https://unpkg.com",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "img-src 'self' data: https:",
      "connect-src 'self' https://api.iustia.net",
      "frame-src 'none'",
      "object-src 'none'",
      "base-uri 'self'",
      "form-action 'self'",
      'upgrade-insecure-requests',
    ].join('; ');

    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = cspPolicy;
    document.head.appendChild(meta);
  }

  // Configurar headers de seguridad
  configureSecurityHeaders() {
    // Estos headers idealmente deben configurarse en el servidor
    const headers = {
      'X-Frame-Options': 'DENY',
      'X-Content-Type-Options': 'nosniff',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
    };

    // Para SPA, algunos headers se pueden simular vía meta tags
    Object.entries(headers).forEach(([name, value]) => {
      const meta = document.createElement('meta');
      meta.httpEquiv = name;
      meta.content = value;
      document.head.appendChild(meta);
    });
  }

  // Deshabilitar funciones peligrosas
  disableDangerousFeatures() {
    // Deshabilitar console en producción
    if (this.environment === 'production') {
      console.log = () => {};
      console.warn = () => {};
      console.error = () => {};
      console.debug = () => {};
    }

    // Deshabilitar click derecho en producción (opcional)
    if (this.environment === 'production') {
      document.addEventListener('contextmenu', e => e.preventDefault());
    }

    // Deshabilitar herramientas de desarrollo (opcional, no 100% efectivo)
    if (this.environment === 'production') {
      setInterval(() => {
        if (window.devtools?.open) {
          window.location.href = 'about:blank';
        }
      }, 1000);
    }
  }

  // Configurar políticas de cookies
  configureCookiePolicy() {
    // Configurar cookies seguras
    document.cookie = 'SameSite=Strict; Secure';

    // Aplicar a todas las cookies existentes
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const [name] = cookie.split('=');
      if (name.trim()) {
        document.cookie = `${name.trim()}=${document.cookie.split(name.trim() + '=')[1]?.split(';')[0] || ''}; SameSite=Strict; Secure; Path=/`;
      }
    });
  }

  // Detectar y prevenir ataques XSS
  setupXSSProtection() {
    // Sanitizar inputs automáticamente
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      const sanitizedValue = SecurityConfig.sanitizeString(value);
      return originalSetItem.call(this, key, sanitizedValue);
    };

    // Interceptar formularios
    document.addEventListener('submit', e => {
      const formData = new FormData(e.target);
      for (let [key, value] of formData.entries()) {
        if (typeof value === 'string') {
          formData.set(key, SecurityConfig.sanitizeString(value));
        }
      }
    });
  }

  // Sanitizar strings para prevenir XSS
  static sanitizeString(str) {
    if (typeof str !== 'string') return str;

    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  // Configurar detección de anomalías
  setupAnomalyDetection() {
    let clickCount = 0;
    let lastClickTime = 0;

    document.addEventListener('click', () => {
      const now = Date.now();

      if (now - lastClickTime < 100) {
        clickCount++;

        if (clickCount > 10) {
          // Posible actividad automatizada
          console.warn('Suspicious rapid clicking detected');
          // Aquí podrías implementar medidas adicionales
        }
      } else {
        clickCount = 0;
      }

      lastClickTime = now;
    });
  }

  // Inicializar todas las configuraciones
  initialize() {
    this.configureCSP();
    this.configureSecurityHeaders();
    this.disableDangerousFeatures();
    this.configureCookiePolicy();
    this.setupXSSProtection();
    this.setupAnomalyDetection();

    console.log('🔒 Security configuration initialized');
  }
}

const securityConfig = new SecurityConfig();
export default securityConfig;
